/** Settings → Reminders: a daily notification at a time the player picks, via Web Push. */
import { html, nothing } from 'lit-html';
import { request } from '../api.js';
import { toast, update } from './app.js';

interface Reminders {
  available: boolean;
  enabled: boolean;
  time: string;
  devices: number;
}

let data: Reminders | null = null;
let loading = false;
let busy = false;
/** Whether this browser has a push subscription. */
let here = false;

const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent);
const standalone = () => matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

async function load() {
  loading = true;
  try {
    data = await request<Reminders>(fetch, '/api/reminders');
    if (supported()) here = !!(await (await navigator.serviceWorker.getRegistration())?.pushManager.getSubscription());
  } catch {
    data = null;
  } finally {
    update();
  }
}

function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64url.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((b64url.length + 3) % 4));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
}

async function save(enabled: boolean, time: string) {
  data = { ...data!, ...(await request<{ enabled: boolean; time: string }>(fetch, '/api/reminders', { method: 'PUT', body: JSON.stringify({ enabled, time }) })) };
}

async function run(fn: () => Promise<void>) {
  busy = true;
  update();
  try {
    await fn();
  } catch (err) {
    toast((err as Error).message, 5000);
  } finally {
    busy = false;
    update();
  }
}

/** Asks for permission, subscribes this browser and turns reminders on. */
const turnOn = () => run(async () => {
  if ((await Notification.requestPermission()) !== 'granted') {
    toast('Notifications are blocked for Thicket. Allow them in your browser’s site settings, then try again.', 6000);
    return;
  }
  const { publicKey } = await request<{ publicKey: string }>(fetch, '/api/push/key');
  // The app registers its service worker on https only; make sure there is one (and it's active).
  if (!(await navigator.serviceWorker.getRegistration())) await navigator.serviceWorker.register('/sw.js');
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription())
    ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }));
  await request(fetch, '/api/push/subscribe', { method: 'POST', body: JSON.stringify(sub.toJSON()) });
  here = true;
  await save(true, data!.time);
  data!.devices = Math.max(data!.devices, 1);
  const [h, m] = data!.time.split(':').map(Number) as [number, number];
  toast(`Reminders on: every day at ${new Date(2000, 0, 1, h, m).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`, 3500);
});

/** Turns reminders off everywhere and forgets this browser. */
const turnOff = () => run(async () => {
  await save(false, data!.time);
  const sub = supported() ? await (await navigator.serviceWorker.getRegistration())?.pushManager.getSubscription() : null;
  if (sub) {
    await request(fetch, '/api/push/subscribe', { method: 'DELETE', body: JSON.stringify({ endpoint: sub.endpoint }) });
    await sub.unsubscribe().catch(() => false);
  }
  here = false;
});

const sendTest = () => run(async () => {
  const r = await request<{ sent: number }>(fetch, '/api/push/test', { method: 'POST', body: '{}' });
  toast(r.sent ? 'Sent. It should arrive in a moment.' : 'No device took it. Turn reminders off and on again here.', 4000);
});

export function remindersView() {
  if (!data && !loading) void load();
  const body = () => {
    if (!data) return html`<p class="muted" style="margin:0">${loading ? 'Loading…' : 'Couldn’t load reminders.'}</p>`;
    if (!data.available) return html`<p class="muted" style="margin:0">Reminders aren’t switched on for Thicket yet.</p>`;
    if (!supported()) {
      return html`<p style="margin:0">${isIOS() && !standalone()
        ? html`On iPhone and iPad, reminders work once Thicket is on your Home Screen: tap <b>Share</b>, then <b>Add to Home Screen</b>, and open Thicket from there.`
        : 'This browser can’t show notifications.'}</p>`;
    }
    return html`
      <p style="margin:0">One gentle note a day with what’s still growing, and nothing on days when everything’s done or you’re resting.</p>
      <label class="field">Remind me at
        <input type="time" .value=${data.time} ?disabled=${busy}
          @change=${(e: Event) => { const t = (e.target as HTMLInputElement).value; if (t) void run(() => save(data!.enabled, t)); }}></label>
      <div class="actions">
        ${data.enabled && here
          ? html`<button ?disabled=${busy} @click=${turnOff}>Turn off</button><button ?disabled=${busy} @click=${sendTest}>Send a test</button>`
          : html`<button class="primary" ?disabled=${busy} @click=${turnOn}>${data.enabled ? 'Also remind me on this device' : 'Turn on reminders'}</button>`}
      </div>
      ${data.enabled ? html`<p class="small muted" style="margin:0">On for ${data.devices} device${data.devices === 1 ? '' : 's'}${here ? ', including this one' : ''}.</p>` : nothing}`;
  };
  return html`<section class="box form"><h2>Reminders</h2>${body()}</section>`;
}
